import { createRouter, createWebHistory } from 'vue-router'
import type { RouteRecordRaw } from 'vue-router'

const routes: RouteRecordRaw[] = [
  { path: '/', name: 'index', component: () => import('@/pages/index.vue') },
  {
    path: '/auth',
    name: 'auth',
    component: () => import('@/pages/auth.vue'),
    children: [
      {
        path: '',
        name: 'auth-index',
        component: () => import('@/pages/auth/index.vue'),
        meta: {
          public: true,
        },
      },
      {
        path: 'signin',
        name: 'auth-signin',
        component: () => import('@/pages/auth/signin.vue'),
        meta: {
          public: true,
        },
      },
      {
        path: 'confirm-action',
        name: 'auth-confirm-action',
        component: () => import('@/pages/auth/confirm-action.vue'),
        meta: {
          public: true,
        },
      },
      {
        path: 'accept-invite',
        name: 'auth-accept-invite',
        component: () => import('@/pages/auth/accept-invite.vue'),
        meta: {
          public: true,
        },
      },
      {
        path: 'forgot-password',
        name: 'auth-forgot-password',
        component: () => import('@/pages/auth/forgot-password.vue'),
        meta: {
          public: true,
        },
      },
      {
        path: 'reset-password',
        name: 'auth-reset-password',
        component: () => import('@/pages/auth/reset-password.vue'),
        meta: {
          public: true,
        },
      },
      {
        path: 'error',
        name: 'auth-error',
        component: () => import('@/pages/auth/error.vue'),
        meta: {
          public: true,
        },
      },
    ],
  },
  {
    path: '/alerts',
    name: 'alerts',
    component: () => import('@/pages/alerts.vue'),
    children: [
      { path: '', name: 'alerts-index', component: () => import('@/pages/alerts/index.vue') },
    ],
  },
  {
    path: '/enginegpt',
    name: 'enginegpt',
    component: () => import('@/pages/enginegpt.vue'),
    children: [
      { path: '', name: 'enginegpt-index', component: () => import('@/pages/enginegpt/index.vue') },
    ],
  },
  {
    path: '/distribution',
    name: 'distribution',
    component: () => import('@/pages/distribution.vue'),
    children: [
      {
        path: '',
        name: 'distribution-index',
        component: () => import('@/pages/distribution/index.vue'),
      },
    ],
  },
  {
    path: '/meetings',
    name: 'meetings',
    component: () => import('@/pages/meetings.vue'),
    children: [
      {
        path: '',
        name: 'meetings-index',
        redirect: {
          name: 'meetings-schedules-index',
        },
      },
      {
        path: 'schedules',
        name: 'meetings-schedules',
        component: () => import('@/pages/meetings/schedules.vue'),
        children: [
          {
            path: '',
            name: 'meetings-schedules-index',
            component: () => import('@/pages/meetings/schedules/index.vue'),
          },
          {
            path: 'create',
            name: 'meetings-schedules-create',
            component: () => import('@/pages/meetings/schedules/create.vue'),
          },
          {
            path: ':id',
            name: 'meetings-schedules-schedule',
            component: () => import('@/pages/meetings/schedules/[id].vue'),
            children: [
              {
                path: '',
                name: 'meetings-schedules-schedule-index',
                // Redirect to edit for now since we don't need the index
                redirect: (to) => {
                  return {
                    name: 'meetings-schedules-schedule-edit',
                    params: {
                      id: to.params.id,
                    },
                  }
                },
              },
              {
                path: 'edit',
                name: 'meetings-schedules-schedule-edit',
                component: () => import('@/pages/meetings/schedules/[id]/edit.vue'),
              },
            ],
          },
        ],
      },
    ],
  },
  {
    path: '/book',
    name: 'book',
    component: () => import('@/pages/book.vue'),
    children: [
      {
        path: '',
        name: 'book-index',
        component: () => import('@/pages/book/index.vue'),
        meta: {
          public: true,
        },
      },
      {
        path: 'schedules',
        name: 'book-schedules',
        component: () => import('@/pages/book/schedules.vue'),
        children: [
          {
            path: ':id',
            name: 'book-schedules-schedule',
            component: () => import('@/pages/book/schedules/[id].vue'),
            children: [
              {
                path: '',
                name: 'book-schedules-schedule-index',
                component: () => import('@/pages/book/schedules/[id]/index.vue'),
                meta: {
                  public: true,
                },
              },
            ],
          },
        ],
      },
      {
        path: 'booking',
        name: 'book-booking',
        component: () => import('@/pages/book/booking.vue'),
        children: [
          {
            path: ':ref',
            name: 'book-booking-ref',
            component: () => import('@/pages/book/booking/[ref].vue'),
            children: [
              {
                path: '',
                name: 'book-booking-ref-index',
                // Redirect to confirmed, we can reclaim this page in future if we need it
                redirect: (to) => {
                  return {
                    name: 'book-booking-ref-confirmed',
                    params: {
                      ref: to.params.ref,
                    },
                  }
                },
                meta: {
                  public: true,
                },
              },
              {
                path: 'cancel',
                name: 'book-booking-ref-cancel',
                component: () => import('@/pages/book/booking/[ref]/cancel.vue'),
                meta: {
                  public: true,
                },
              },
              {
                path: 'reschedule',
                name: 'book-booking-ref-reschedule',
                component: () => import('@/pages/book/booking/[ref]/reschedule.vue'),
                meta: {
                  public: true,
                },
              },
            ],
          },
          {
            path: 'confirmed',
            name: 'book-booking-confirmed',
            component: () => import('@/pages/book/booking/confirmed.vue'),
            meta: {
              public: true,
            },
          },
        ],
      },
    ],
  },
  {
    path: '/settings',
    name: 'settings',
    component: () => import('@/pages/settings.vue'),
    children: [
      { path: '', name: 'settings-index', component: () => import('@/pages/settings/index.vue') },
      {
        path: 'account',
        name: 'settings-account',
        component: () => import('@/pages/settings/account.vue'),
        children: [
          {
            path: '',
            name: 'settings-account-index',
            component: () => import('@/pages/settings/account/index.vue'),
          },
          {
            path: 'security',
            name: 'settings-account-security',
            component: () => import('@/pages/settings/account/security.vue'),
          },
          {
            path: 'alerts',
            name: 'settings-account-alerts',
            component: () => import('@/pages/settings/account/alerts.vue'),
          },
          {
            path: 'preferences',
            name: 'settings-account-preferences',
            component: () => import('@/pages/settings/account/preferences.vue'),
          },
        ],
      },
      {
        path: 'organization',
        name: 'settings-organization',
        component: () => import('@/pages/settings/organization.vue'),
        children: [
          {
            path: '',
            name: 'settings-organization-index',
            component: () => import('@/pages/settings/organization/index.vue'),
          },
          {
            path: 'users',
            name: 'settings-organization-users',
            component: () => import('@/pages/settings/organization/users.vue'),
          },
          {
            path: 'integrations',
            name: 'settings-organization-integrations',
            component: () => import('@/pages/settings/organization/integrations.vue'),
          },
        ],
      },
      {
        path: 'products',
        name: 'settings-products',
        component: () => import('@/pages/settings/products.vue'),
        children: [
          {
            path: 'meetings',
            name: 'settings-products-meetings',
            component: () => import('@/pages/settings/products/meetings.vue'),
          },
        ],
      },
    ],
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/pages/error-404.vue'),
    meta: {
      public: true,
    },
  },
]

const router = createRouter({
  history: createWebHistory(),
  routes,
})

export default router
